import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSortBy } from 'react-instantsearch';
import { Item, Portal } from '@radix-ui/react-dropdown-menu';
import { useLocaleContext } from '../../../context/localeContext';
import { ArrowDown } from '../../../icons';
import { createSortByItems } from '../../../utils/algoliaSortByUtils';
import { createClassName } from '../../../utils/createClassName';
import { Container, DropdownContent, Label, RadioButton, RadioGroup, TriggerButton } from './SortByMenu.styles';

type UseAlgoliaFilterReturn = ReturnType<typeof useSortBy>;

export function SortBy({ refine, currentRefinement, options }: UseAlgoliaFilterReturn) {
  const { t } = useTranslation(['lib-global-common']);

  const [isDropDownOpen, setDropDownOpen] = useState(false);
  const [isDefault, setIsDefault] = useState(true);

  const [renderPortalContent, setRenderPortalContent] = useState(false);

  useEffect(() => {
    // Allows screen readers to announce expanded state
    setRenderPortalContent(isDropDownOpen);
  }, [isDropDownOpen]);

  const handleOpenDropDown = () => {
    setDropDownOpen((prev) => !prev);
  };

  const handleOnChangeValue = (value: string) => () => {
    if (value === currentRefinement) return;
    if (isDefault) {
      setIsDefault(false);
    }
    handleOpenDropDown();
    refine(value);
  };

  const getLabel = (currentSort: string) => options.find(({ value }) => value === currentSort)?.label || '';

  const triggerLabel = t(isDefault ? 'sort.by.menu.sort.by' : getLabel(currentRefinement)).trim();

  return (
    <Container open={isDropDownOpen} onOpenChange={handleOpenDropDown} modal={false}>
      <TriggerButton data-testid="sortByButton" role="combobox" aria-expanded={isDropDownOpen}>
        <Label className="DropdownMenuLabel">{triggerLabel}</Label>
        <ArrowDown className="accordion-arrow-down" />
      </TriggerButton>

      <Portal>
        {renderPortalContent && (
          <DropdownContent data-testid="sortByDropdown" $isDropDownOpen={isDropDownOpen}>
            <RadioGroup id="sort-by" label={triggerLabel} $isDropDownOpen={isDropDownOpen}>
              {options.map(({ value, label }) => (
                <Item key={value} onSelect={handleOnChangeValue(value)}>
                  <RadioButton
                    $isDropDownOpen={isDropDownOpen}
                    label={t(label)}
                    value={value}
                    name="sort-by"
                    onChange={handleOnChangeValue(value)}
                    checked={currentRefinement === value}
                    className={createClassName({ checked: currentRefinement === value })}
                  />
                </Item>
              ))}
            </RadioGroup>
          </DropdownContent>
        )}
      </Portal>
    </Container>
  );
}

export function SortByMenu() {
  const { locale } = useLocaleContext();
  const items = createSortByItems(locale);
  const sortBy = useSortBy({
    items,
  });

  return <SortBy {...sortBy} />;
}
export default SortByMenu;
